import {
  getItemIcon,
  ItemCategory,
  type ItemCategoryValue,
  type ItemTypeValue,
  type TranslationKey,
} from '@dreamgame/shared';
import { tv } from 'tailwind-variants';
import { t } from '../../state/i18n';
import {
  activeStockpileCategory,
  closeStockpilePanel,
  filteredStockpileItems,
  isStockpilePanelOpen,
  type StockpileItemInfo,
  setStockpileCategory,
  setStockpileSearch,
  stockpileCategories,
  stockpileCategoryCounts,
  stockpileSearchQuery,
  stockpileTotalValue,
} from '../../state/stockpile';

// ============================================================================
// StockpilePanel - Fantasy Medieval Inventory Ledger UI
// ============================================================================

// Styles using tailwind-variants - Fantasy Medieval Theme (matches ConstructionPanel)
const panel = tv({
  slots: {
    // Positioned to match ConstructionPanel location
    root: [
      'fixed left-[1.1em] bottom-[6.2em]',
      'z-[1000]',
      'flex items-start',
      'animate-fade-in',
      'text-[16px]',
      'subpixel-antialiased',
    ],

    // The Book Spine (Vertical bar between tabs and page)
    spine: [
      'w-[1.2em] h-[23.8em]', // Slightly taller than ConstructionPanel
      'bg-gradient-to-r from-leather-dark via-leather to-leather-dark',
      'border-y-2 border-l-2 border-leather-border',
      'rounded-l-md',
      'shadow-xl',
      'z-20',
      'relative',
    ],

    // The Main Page (Right side)
    container: [
      'relative',
      'w-[24em] h-[22.5em]', // Wider than ConstructionPanel
      'bg-parchment',
      'bg-[radial-gradient(ellipse_at_center,_var(--color-parchment-light)_0%,_var(--color-parchment)_100%)]',
      'border-y-4 border-r-4 border-leather',
      'rounded-r-lg',
      'shadow-tome',
      'flex flex-col',
      'z-10',
      'after:content-[""]',
      'after:absolute after:inset-[0.45em]',
      'after:border-2 after:border-gold-filigree/30',
      'after:rounded-r-md',
      'after:pointer-events-none',
    ],

    // Decorative corner flourishes
    cornerDecorTop: [
      'absolute top-[0.55em] right-[0.55em] w-[2em] h-[2em]',
      'border-t-2 border-r-2 border-gold-filigree',
      'rounded-tr-lg',
      'pointer-events-none',
    ],
    cornerDecorBottom: [
      'absolute bottom-[0.55em] right-[0.55em] w-[2em] h-[2em]',
      'border-b-2 border-r-2 border-gold-filigree',
      'rounded-br-lg',
      'pointer-events-none',
    ],

    // Header within the page
    header: [
      'relative',
      'flex items-center justify-between',
      'py-[0.8em] px-[1.45em]',
      'mx-[1em] mt-[0.55em]',
      'border-b border-gold-filigree/50',
    ],

    title: ['font-fantasy text-ink text-[18px] tracking-widest', 'uppercase', 'font-bold'],

    closeButton: [
      'absolute -right-[0.65em] -top-[0.65em]',
      'w-[2.15em] h-[2.15em]',
      'flex items-center justify-center',
      'rounded-full',
      'bg-leather-dark',
      'border-2 border-gold-filigree',
      'text-gold-bright',
      'hover:scale-110',
      'hover:bg-wax-seal',
      'transition-all duration-200',
      'cursor-pointer',
      'shadow-lg',
      'z-50',
    ],

    // Search bar
    searchContainer: ['px-[1.45em] py-[0.45em]'],

    searchInput: [
      'w-full',
      'px-[0.8em] py-[0.42em]',
      'bg-parchment-dark/30',
      'border border-gold-filigree/30',
      'rounded-[0.4em]',
      'text-[13px] text-ink',
      'font-medieval',
      'placeholder:text-ink-faded placeholder:italic',
      'focus:outline-none focus:border-gold-filigree',
      'transition-colors duration-200',
    ],

    // Side Tabs (Bookmarks)
    tabContainer: ['flex flex-col gap-[0.28em]', 'mt-[2.1em]', '-mr-[0.5em]', 'z-0'],

    tab: [
      'w-[8.8em]',
      'h-[2.45em]',
      'pl-[0.75em] pr-[1em]',
      'flex items-center justify-between gap-[0.3em]',
      'bg-leather',
      'border-y border-l border-gold-filigree',
      'rounded-l-full',
      'text-[12px] font-fantasy tracking-wider font-bold',
      'text-parchment-dark',
      'shadow-md',
      'transition-all duration-200',
      'cursor-pointer',
      'hover:translate-x-[-4px]',
      'relative',
    ],

    tabActive: [
      'bg-leather-light',
      'text-gold-bright',
      'border-gold-bright',
      'translate-x-[12px]',
      'z-30',
      'shadow-lg',
    ],

    tabLabel: ['flex-1', 'truncate'],
    tabCount: [
      'text-[10px]',
      'px-[0.32em] py-[0.12em]',
      'bg-ink/20',
      'rounded-full',
      'min-w-[1.4em]',
      'text-center',
    ],

    // Content area
    content: [
      'flex-1',
      'px-[1.45em] py-[0.45em]',
      'overflow-y-auto',
      'scrollbar-thin scrollbar-thumb-leather scrollbar-track-transparent',
    ],

    // Item list
    itemList: ['flex flex-col gap-[0.28em]'],

    // Item row
    itemRow: [
      'flex items-center gap-[0.75em]',
      'px-[0.8em] py-[0.5em]',
      'rounded-[0.4em]',
      'hover:bg-ink/5',
      'transition-colors duration-150',
      'group',
    ],

    itemIcon: [
      'w-[2.15em] h-[2.15em]',
      'flex items-center justify-center',
      'bg-parchment-dark/30',
      'rounded-[0.3em]',
      'border border-gold-filigree/20',
    ],

    itemIconImg: ['w-[1.55em] h-[1.55em] object-contain'],

    itemIconFallback: ['text-[1.05em]'],

    itemInfo: ['flex-1 min-w-0'],

    itemName: ['text-[13px] font-bold text-ink', 'font-medieval', 'truncate'],

    itemCategory: ['text-[11px] text-ink-faded', 'leading-none'],

    itemQuantity: ['text-[13px] font-bold text-ink', 'font-mono', 'tabular-nums'],

    itemValue: ['text-[11px] text-gold-dark', 'font-mono'],

    // Empty state
    emptyState: [
      'flex flex-col items-center justify-center',
      'h-full',
      'text-ink-faded text-[13px] italic',
      'text-center',
    ],

    // Footer
    footer: [
      'px-[1.45em] py-[0.5em]',
      'border-t border-gold-filigree/30',
      'flex items-center justify-between',
      'mx-[1em] mb-[0.55em]',
    ],

    footerLabel: ['text-[11px] text-ink-faded', 'font-fantasy uppercase tracking-wider'],
    footerValue: ['text-[13px] font-bold text-gold-dark', 'font-mono'],
  },
});

// Category labels mapping - uses i18n keys
const CATEGORY_KEYS: Record<ItemCategoryValue, TranslationKey> = {
  [ItemCategory.NONE]: 'ui.tabs.all',
  [ItemCategory.RAW_MATERIAL]: 'ui.tabs.raw',
  [ItemCategory.PROCESSED_MATERIAL]: 'ui.tabs.processed',
  [ItemCategory.FOOD]: 'ui.tabs.food',
  [ItemCategory.TOOL]: 'ui.tabs.tools',
  [ItemCategory.WEAPON]: 'ui.tabs.weapons',
  [ItemCategory.ARMOR]: 'ui.tabs.armor',
  [ItemCategory.CONSUMABLE]: 'ui.tabs.consumable',
  [ItemCategory.AMMUNITION]: 'ui.tabs.ammunition',
};

const getCategoryLabel = (category: ItemCategoryValue): string => {
  return t(CATEGORY_KEYS[category] ?? 'ui.tabs.all');
};

const ItemIcon = ({ itemType, name }: { itemType: ItemTypeValue; name: string }) => {
  const styles = panel();
  const iconPath = getItemIcon({ itemType });

  if (iconPath) {
    return (
      <div class={styles.itemIcon()}>
        {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: onError is not interactive */}
        <img
          src={`/items/${iconPath}`}
          alt={name}
          class={styles.itemIconImg()}
          onError={(e) => {
            // Hide img and show fallback
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>
    );
  }

  // Fallback to first letter
  return (
    <div class={styles.itemIcon()}>
      <span class={styles.itemIconFallback()}>{name.charAt(0)}</span>
    </div>
  );
};

const StockpileItem = ({ item }: { item: StockpileItemInfo }) => {
  const styles = panel();

  return (
    <div class={styles.itemRow()}>
      <ItemIcon itemType={item.itemType} name={item.name} />
      <div class={styles.itemInfo()}>
        <div class={styles.itemName()}>{item.name}</div>
        <div class={styles.itemCategory()}>{getCategoryLabel(item.category)}</div>
      </div>
      <div class="flex flex-col items-end">
        <span class={styles.itemQuantity()}>x{item.quantity}</span>
        <span class={styles.itemValue()}>{item.totalValue}g</span>
      </div>
    </div>
  );
};

export const StockpilePanel = () => {
  const styles = panel();

  if (!isStockpilePanelOpen.value) {
    return null;
  }

  const currentCategory = activeStockpileCategory.value;
  const items = filteredStockpileItems.value;
  const categories = stockpileCategories.value;
  const categoryCounts = stockpileCategoryCounts.value;
  const totalValue = stockpileTotalValue.value;
  const searchQuery = stockpileSearchQuery.value;

  const handleTabClick = ({ category }: { category: ItemCategoryValue }) => {
    setStockpileCategory({ category });
  };

  const handleSearchChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    setStockpileSearch({ query: target.value });
  };

  const handleClose = () => {
    closeStockpilePanel();
  };

  // Format count for display
  const formatCount = (count: number): string => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  return (
    <div class={styles.root()}>
      {/* Left Tabs (Bookmarks) */}
      <div class={styles.tabContainer()}>
        {categories.map((cat) => {
          const count = categoryCounts.get(cat.id) ?? 0;
          const isActive = currentCategory === cat.id;
          return (
            <button
              type="button"
              key={cat.id}
              class={`${styles.tab()} ${isActive ? styles.tabActive() : ''}`}
              onClick={() => handleTabClick({ category: cat.id })}
            >
              <div class="absolute left-[0.25em] top-1/2 -translate-y-1/2 w-[0.12em] h-[1.6em] border-l border-gold-filigree opacity-50" />
              <span class={styles.tabLabel()}>{cat.name}</span>
              {count > 0 && <span class={styles.tabCount()}>{formatCount(count)}</span>}
            </button>
          );
        })}
      </div>

      {/* Book Spine */}
      <div class={styles.spine()} />

      {/* Main Page */}
      <div class={styles.container()}>
        {/* Close Button */}
        <button type="button" class={styles.closeButton()} onClick={handleClose} aria-label="Close">
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            class="w-[1em] h-[1em]"
            role="img"
            aria-hidden="true"
          >
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
          </svg>
        </button>

        {/* Decorative Corners */}
        <div class={styles.cornerDecorTop()} />
        <div class={styles.cornerDecorBottom()} />

        {/* Page Header */}
        <div class={styles.header()}>
          <h2 class={styles.title()}>{t('ui.panels.stockpile')}</h2>
        </div>

        {/* Search Bar */}
        <div class={styles.searchContainer()}>
          <input
            type="text"
            class={styles.searchInput()}
            placeholder={t('ui.search.placeholder')}
            value={searchQuery}
            onInput={handleSearchChange}
          />
        </div>

        {/* Item List Content */}
        <div class={styles.content()}>
          {items.length === 0 ? (
            <div class={styles.emptyState()}>
              {searchQuery ? (
                <>
                  <span>{t('ui.empty.noItemsMatch', { query: searchQuery })}</span>
                  <span class="text-[11px] mt-[0.25em]">{t('ui.empty.tryDifferentSearch')}</span>
                </>
              ) : (
                <>
                  <span>{t('ui.empty.noItems')}</span>
                  <span class="text-[11px] mt-[0.25em]">{t('ui.empty.gatherResources')}</span>
                </>
              )}
            </div>
          ) : (
            <div class={styles.itemList()}>
              {items.map((item) => (
                <StockpileItem key={item.itemType} item={item} />
              ))}
            </div>
          )}
        </div>

        {/* Footer with total value */}
        <div class={styles.footer()}>
          <span class={styles.footerLabel()}>{t('ui.labels.totalValue')}</span>
          <span class={styles.footerValue()}>
            {t('ui.labels.gold', { value: totalValue.toLocaleString() })}
          </span>
        </div>
      </div>
    </div>
  );
};
